/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from 'Context';
import { axios as apiAxios, dataFetcher, endpoints, getToken } from 'Api';
import { ReactComponent as EditIcon } from 'Assets/edit.svg';
import styles from './index.module.css';

// Module-level cache — persists across mount/unmount so the previous value is shown
// immediately on revisit while a fresh fetch silently refreshes it in the background.
let displayNameCache = null;

const DisplayNameSettings = () => {
  const { setAppSnackbar, langText, autoSignIn } = useContext(AppContext);
  const layoutText = langText?.layout;
  const commonText = langText?.common;
  const [displayName, setDisplayName] = useState(displayNameCache || '');
  const [draftName, setDraftName] = useState(displayNameCache || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gettingDisplayName, setGettingDisplayName] = useState(displayNameCache === null);

  const fetchDisplayName = async () => {
    if (displayNameCache === null) setGettingDisplayName(true);
    try {
      const res = await apiAxios.get(endpoints.displayName, { params: { token: getToken() } });
      const body = res?.data;
      if (body?.success) {
        const name = body?.display_name || body?.data?.display_name || '';
        displayNameCache = name;
        setDisplayName((prev) => (prev === name ? prev : name));
        setDraftName((prev) => (prev === name ? prev : name));
      }
    } catch (e) {
      if (displayNameCache === null) {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching display name' });
      }
    } finally {
      setGettingDisplayName(false);
    }
  };

  useEffect(() => { fetchDisplayName(); }, []);

  const handleEdit = () => {
    setDraftName(displayName || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftName(displayName || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = (draftName || '').trim();
    setIsSaving(true);
    try {
      const res = await dataFetcher(endpoints.displayName, { display_name: trimmed });
      if (res?.success) {
        displayNameCache = trimmed;
        setDisplayName(trimmed);
        setIsEditing(false);
        if (autoSignIn) autoSignIn();
        setAppSnackbar({
          isVisible: true,
          message: res?.message || 'Display name updated',
        });
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: res?.message || 'Oops! Something went wrong while updating display name',
        });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while updating display name' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.slideShowContainer}>
      <div className={styles.chooseOrder}>
        {layoutText?.displayName || 'Display Name'}
      </div>
      <div className={styles.albumSettingsBundle}>
        <div className={styles.displayNameRow}>
          <div className={styles.displayNameValueWrap}>
            <input
              type="text"
              className={styles.displayNameInput}
              value={isEditing ? draftName : (displayName ? `${displayName}'s Projector` : '')}
              placeholder={layoutText?.enterDisplayName || 'Enter display name'}
              onChange={(e) => setDraftName(e.target.value)}
              disabled={!isEditing || isSaving}
              autoFocus={isEditing}
            />
            {isEditing ? (
              <div className={styles.displayNameButtons}>
                <button
                  type="button"
                  className={styles.displayNameCancelButton}
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  {commonText?.cancel || 'Cancel'}
                </button>
                <button
                  type="button"
                  className={`${styles.displayNameSaveButton} ${
                    (draftName || '').trim() && (draftName || '').trim() !== (displayName || '').trim()
                      ? styles.displayNameSaveButtonActive
                      : ''
                  }`}
                  onClick={handleSave}
                  disabled={isSaving || gettingDisplayName || (draftName || '').trim() === (displayName || '').trim() || !(draftName || '').trim()}
                >
                  {isSaving ? `${commonText?.saving || 'Saving'}...` : (commonText?.save || 'Save')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.displayNameEditButton}
                onClick={handleEdit}
                disabled={gettingDisplayName}
              >
                <EditIcon className={styles.displayNameEditIcon} />
                <span>{commonText?.edit || 'Edit'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayNameSettings;
